import React, { useEffect, useMemo, useRef } from "react";
import { BlockNoteSchema, defaultBlockSpecs } from "@blocknote/core";
import { es } from "@blocknote/core/locales";
import { BlockNoteView } from "@blocknote/mantine";
import { useCreateBlockNote } from "@blocknote/react";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";

const getDocumentBlocks = value =>
  value?.type === "blocknote" && Array.isArray(value.blocks) ? value.blocks : [];

const allowedTextBlockSpecs = {
  paragraph: defaultBlockSpecs.paragraph,
  heading: defaultBlockSpecs.heading,
  bulletListItem: defaultBlockSpecs.bulletListItem,
  numberedListItem: defaultBlockSpecs.numberedListItem,
  checkListItem: defaultBlockSpecs.checkListItem,
  quote: defaultBlockSpecs.quote,
  codeBlock: defaultBlockSpecs.codeBlock,
  table: defaultBlockSpecs.table
};

const allowedTextBlockTypes = new Set(Object.keys(allowedTextBlockSpecs));

const normalizeTextContent = content => {
  if (!Array.isArray(content)) return [];
  return content.filter(item => item && typeof item === "object" && item.type === "text");
};

const normalizeBlockContent = block => {
  if (block.type === "table") return block.content;
  return normalizeTextContent(block.content);
};

const normalizeTextBlock = block => {
  if (!block || typeof block !== "object") return [];

  const children = Array.isArray(block.children)
    ? block.children.flatMap(normalizeTextBlock)
    : [];

  if (!allowedTextBlockTypes.has(block.type)) {
    const content = normalizeTextContent(block.content);
    return content.length
      ? [{ type: "paragraph", content, children }]
      : children;
  }

  return [
    {
      ...block,
      content: normalizeBlockContent(block),
      children
    }
  ];
};

const getTextOnlyBlocks = value => getDocumentBlocks(value).flatMap(normalizeTextBlock);

const getInitialContent = value => {
  const blocks = getTextOnlyBlocks(value);
  return blocks.length ? blocks : undefined;
};

const getEditableBlocks = value => {
  const blocks = getTextOnlyBlocks(value);
  return blocks.length ? blocks : [{ type: "paragraph" }];
};

const getDocumentSignature = blocks => {
  try {
    return JSON.stringify(blocks || []);
  } catch {
    return "";
  }
};

const blockNoteInteractiveSelector = [
  ".bn-block-content",
  ".bn-block",
  ".bn-block-outer",
  ".bn-side-menu",
  ".bn-drag-handle-menu",
  ".bn-toolbar",
  ".bn-panel",
  ".bn-suggestion-menu",
  ".mantine-Menu-dropdown",
  "button",
  "[draggable='true']",
  "[role='button']",
  "[role='menuitem']"
].join(",");

const textOnlySchema = BlockNoteSchema.create({
  blockSpecs: allowedTextBlockSpecs
});

const BlockDocument = ({ className = "", value, onChange, themeMode = "light" }) => {
  const saveTimerRef = useRef(null);
  const latestChangeRef = useRef(onChange);
  const applyingRemoteValueRef = useRef(false);
  const draggingBlockRef = useRef(false);
  const pendingLocalChangeRef = useRef(false);
  const pendingDragSaveRef = useRef(false);
  const documentSignatureRef = useRef(getDocumentSignature(getTextOnlyBlocks(value)));
  const valueSignature = useMemo(
    () => getDocumentSignature(getTextOnlyBlocks(value)),
    [value]
  );
  const editor = useCreateBlockNote({
    dictionary: es,
    initialContent: getInitialContent(value),
    schema: textOnlySchema
  });

  useEffect(() => {
    latestChangeRef.current = onChange;
  }, [onChange]);

  useEffect(
    () => () => {
      window.clearTimeout(saveTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (valueSignature === documentSignatureRef.current) return;
    if (pendingLocalChangeRef.current) return;

    window.clearTimeout(saveTimerRef.current);
    applyingRemoteValueRef.current = true;
    editor.replaceBlocks(editor.document, getEditableBlocks(value));
    documentSignatureRef.current = valueSignature;
    window.setTimeout(() => {
      applyingRemoteValueRef.current = false;
    }, 0);
  }, [editor, value, valueSignature]);

  const focusLastBlock = event => {
    const target = event.target;
    if (target?.closest?.(blockNoteInteractiveSelector)) return;

    const lastBlock = editor.document[editor.document.length - 1];
    if (!lastBlock) return;

    event.preventDefault();
    editor.focus();
    editor.setTextCursorPosition(lastBlock, "end");
  };

  const saveDocument = () => {
    const blocks = editor.document;
    documentSignatureRef.current = getDocumentSignature(blocks);
    pendingLocalChangeRef.current = false;
    latestChangeRef.current?.({
      type: "blocknote",
      blocks
    });
  };

  const scheduleSave = (delay = 500) => {
    window.clearTimeout(saveTimerRef.current);
    pendingLocalChangeRef.current = true;
    saveTimerRef.current = window.setTimeout(saveDocument, delay);
  };

  const startBlockDrag = event => {
    const target = event.target;
    if (!target?.closest?.(".bn-side-menu [draggable='true'],.bn-side-menu button[draggable='true']")) return;

    draggingBlockRef.current = true;
    pendingDragSaveRef.current = false;
    window.clearTimeout(saveTimerRef.current);
  };

  const finishBlockDrag = () => {
    if (!draggingBlockRef.current) return;

    draggingBlockRef.current = false;
    if (pendingDragSaveRef.current) {
      pendingDragSaveRef.current = false;
      scheduleSave();
    }
  };

  return (
    <div
      className={`block-document ${className}`}
      onMouseDown={focusLastBlock}
      onDragStartCapture={startBlockDrag}
      onDragEndCapture={finishBlockDrag}
      onDropCapture={finishBlockDrag}
    >
      <BlockNoteView
        editor={editor}
        theme={themeMode === "dark" ? "dark" : "light"}
        onChange={() => {
          if (applyingRemoteValueRef.current) return;
          if (draggingBlockRef.current) {
            pendingDragSaveRef.current = true;
            return;
          }
          scheduleSave();
        }}
      />
    </div>
  );
};

export default BlockDocument;
